(() => {
  class Quiz {
    constructor(root) {
      this.root = root;
      this.steps = [...root.querySelectorAll('[data-step]')];
      this.questionSteps = [...root.querySelectorAll('[data-step="question"]')];
      this.resultSteps = [...root.querySelectorAll('[data-step="result"]')];
      this.dots = [...root.querySelectorAll('[data-dot]')];
      this.progress = root.querySelector('[data-progress]');
      this.introStep = root.querySelector('[data-step="intro"]');
      this.hasIntro = Boolean(this.introStep);
      this.selections = new Map(); // question index -> number[] of points
      this.responses = new Map(); // question index -> { question, answer } human-readable
      this.current = 0;

      this.nameInput = this.introStep ? this.introStep.querySelector('[data-quiz-name]') : null;
      this.emailInput = this.introStep ? this.introStep.querySelector('[data-quiz-email]') : null;
      this.startBtn = this.introStep ? this.introStep.querySelector('[data-action="start"]') : null;
      this.lead = { name: '', email: '' };
      this.answerProps = {}; // Klaviyo profile property key -> selected answer value
      this.klaviyoCompany = root.dataset.klaviyoCompanyId || '';
      this.klaviyoListId = root.dataset.klaviyoListId || '';
     
      this.customerId = root.dataset.customerId || '';
      this.customerEmail = root.dataset.customerEmail || '';

      this.bindEvents();
      this.start();
    }

    showOnly(target) {
      this.steps.forEach((step) => {
        step.hidden = step !== target;
      });
    }

    setProgressVisible(visible) {
      if (this.progress) this.progress.hidden = !visible;
    }

    updateDots(index) {
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    goToQuestion(index) {
      const step = this.questionSteps[index];
      if (!step) return;
      this.current = index;
      this.showOnly(step);
      this.updateDots(index);
      this.setProgressVisible(true);
      const next = step.querySelector('[data-action="next"]');
      if (next) next.disabled = !this.selections.has(index);
    }

    showIntro() {
      if (!this.hasIntro) return;
      this.current = 0;
      this.setProgressVisible(false);
      this.showOnly(this.introStep);
    }

    // --- Lead capture (Klaviyo) -------------------------------------------------

    hasLeadForm() {
      return Boolean(this.nameInput && this.emailInput);
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    validateLead() {
      const name = this.nameInput ? this.nameInput.value.trim() : '';
      const email = this.emailInput ? this.emailInput.value.trim() : '';
      const valid = name.length > 0 && this.isValidEmail(email);
      if (this.startBtn) this.startBtn.disabled = !valid;
      return valid;
    }

    
    applyLoggedInCustomer() {
      if (!this.customerEmail) return;
      if (this.emailInput && !this.emailInput.value) this.emailInput.value = this.customerEmail;
      this.validateLead();
    }

    handleStart() {
      if (this.hasLeadForm()) {
        if (!this.validateLead()) {
          if (this.emailInput && this.emailInput.reportValidity) this.emailInput.reportValidity();
          return;
        }
        this.lead.name = this.nameInput.value.trim();
        this.lead.email = this.emailInput.value.trim();
      
        this.identifyLead({ quiz_started: 'true' });
        this.subscribeToList({ quiz_started: 'true' });
      }
      this.goToQuestion(0);
    }

    // The signup request: subscribes the lead to a Klaviyo list with marketing
    // consent via the client Subscriptions API. Requires company id + list id.
    subscribeToList(extraProps) {
      if (!this.hasLeadForm() || !this.lead.email) return;
      if (!this.klaviyoCompany || !this.klaviyoListId) {
        console.warn('[quiz] Signup NOT sent — set the Klaviyo company id AND list id in the Quiz section settings.', {
          company_id: this.klaviyoCompany || '(empty)',
          list_id: this.klaviyoListId || '(empty)',
        });
        return;
      }
      const parts = this.lead.name.split(' ');
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: this.lead.email,
                  first_name: parts[0] || '',
                  last_name: parts.slice(1).join(' '),
                  properties: { ...this.answerProps, ...extraProps },
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: this.klaviyoListId } } },
        },
      };
      const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.klaviyoCompany}`;
      console.log('[quiz] Signup → Klaviyo', url, payload);
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2025-07-15' },
        body: JSON.stringify(payload),
      })
        .then((response) => console.log('[quiz] Signup response:', response.status))
        .catch((error) => console.error('[quiz] Signup failed:', error));
    }

    // Klaviyo's onsite SDK (loaded by the installed Klaviyo app). Both the modern
    // `window.klaviyo` and legacy `window._learnq` objects expose `.push([...])`.
    klaviyoPush(args) {
      const kl = window.klaviyo || window._learnq;
      if (!kl || typeof kl.push !== 'function') {
        console.warn('[quiz] Klaviyo onsite SDK not found on window — is the Klaviyo app embed enabled on this page?');
        return false;
      }
      kl.push(args);
      return true;
    }

    identifyLead(extraProps) {
      if (!this.hasLeadForm() || !this.lead.email) return;
      const parts = this.lead.name.split(' ');
      const props = {
        $email: this.lead.email,
        $first_name: parts[0] || '',
        $last_name: parts.slice(1).join(' '),
        ...this.answerProps,
        ...extraProps,
      };
    
      if (this.customerId) props.shopify_customer_id = this.customerId;
      console.log('[quiz] Klaviyo identify →', props);
      this.klaviyoPush(['identify', props]);
    }

    completeKlaviyo(winner, target) {
      if (!this.hasLeadForm()) return;
      const resultTitle = target ? target.querySelector('.quiz__result-title') : null;
      const product = resultTitle ? resultTitle.textContent.trim() : '';
      // Update the profile with answers + result...
      this.identifyLead({
        quiz_completed: 'true',
        quiz_result_category: String(winner),
        quiz_result_product: product,
      });
      // ...and record a trackable event for flows/segments.
      const eventProps = {
        ...this.answerProps,
        result_category: String(winner),
        result_product: product,
      };
      console.log('[quiz] Klaviyo track "Completed Quiz" →', eventProps);
      this.klaviyoPush(['track', 'Completed Quiz', eventProps]);
    }

    pickWinningCategory() {
      const totals = [0, 0, 0, 0];
      for (const points of this.selections.values()) {
        points.forEach((value, i) => {
          totals[i] += value || 0;
        });
      }
      return totals.reduce((best, value, i) => (value > totals[best] ? i : best), 0);
    }

    finish() {
      const winner = this.pickWinningCategory();
      const target =
        this.resultSteps.find((panel) => Number(panel.dataset.categoryIndex) === winner) ??
        this.resultSteps[0];
      this.setProgressVisible(false);
      if (target) this.showOnly(target);
      this.completeKlaviyo(winner, target);
    }

    selectAnswer(index, answer, siblings, next) {
      siblings.forEach((sibling) => sibling.classList.remove('is-selected'));
      answer.classList.add('is-selected');
      this.selections.set(index, answer.dataset.points.split(',').map(Number));
      const step = this.questionSteps[index];
      const questionEl = step ? step.querySelector('.quiz__question') : null;
      const labelEl = answer.querySelector('.quiz__answer-label') || answer;
      this.responses.set(index, {
        question: questionEl ? questionEl.textContent.trim() : `Question ${index + 1}`,
        answer: labelEl.textContent.trim(),
      });
     
      const key = answer.dataset.key;
      if (key) this.answerProps[key] = answer.dataset.value || labelEl.textContent.trim();
      if (next) next.disabled = false;
    }

    reset() {
      this.selections.clear();
      this.responses.clear();
      this.answerProps = {};
      this.questionSteps.forEach((step) => {
        step.querySelectorAll('[data-points]').forEach((answer) => {
          answer.classList.remove('is-selected');
        });
        const next = step.querySelector('[data-action="next"]');
        if (next) next.disabled = true;
      });
      if (this.hasIntro) this.showIntro();
      else this.goToQuestion(0);
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    bindEvents() {
      this.questionSteps.forEach((step, index) => {
        const answers = [...step.querySelectorAll('[data-points]')];
        const next = step.querySelector('[data-action="next"]');
        answers.forEach((answer) => {
          answer.addEventListener('click', () => this.selectAnswer(index, answer, answers, next));
        });
      });

      this.root.querySelectorAll('[data-action="start"]').forEach((btn) => {
        btn.addEventListener('click', () => this.handleStart());
      });

      if (this.nameInput) this.nameInput.addEventListener('input', () => this.validateLead());
      if (this.emailInput) {
        this.emailInput.addEventListener('input', () => this.validateLead());
        this.emailInput.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') this.handleStart();
        });
      }

      this.root.querySelectorAll('[data-action="next"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (this.current < this.questionSteps.length - 1) this.goToQuestion(this.current + 1);
          else this.finish();
        });
      });

      this.root.querySelectorAll('[data-action="back"]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (this.current > 0) this.goToQuestion(this.current - 1);
          else this.showIntro();
        });
      });

      this.root.querySelectorAll('[data-action="retake"]').forEach((btn) => {
        btn.addEventListener('click', () => this.reset());
      });
    }

    start() {
      this.applyLoggedInCustomer();
      if (this.hasLeadForm()) this.validateLead();
      if (this.hasIntro) this.showIntro();
      else if (this.questionSteps.length) this.goToQuestion(0);
    }
  }

  const init = () => {
    document.querySelectorAll('[data-quiz]').forEach((root) => {
      if (root.dataset.quizReady === 'true') return;
      root.dataset.quizReady = 'true';
      new Quiz(root);
    });
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
